import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  User, Heart, Shield, FileText, Phone, ClipboardCheck,
  AlertTriangle, Home, CheckCircle, ChevronRight, ChevronLeft, ChevronDown,
  Save, Send, Loader2, Upload, FileSignature
} from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { safeJsonRequest } from "../utils/safeFetch";

import { downloadBlankIntakePacket, downloadPRPInfoSheet } from "../utils/intakePacketPdf";

const SECTIONS = [
  { key: "personalInfo", label: "Personal Information", icon: User },
  { key: "emergencyContact", label: "Emergency Contact", icon: Phone },
  { key: "insurance", label: "Insurance", icon: Shield },
  { key: "referralInfo", label: "Service Information", icon: FileText },
  { key: "presentingConcerns", label: "Presenting Concerns", icon: Heart },
  { key: "medicalHistory", label: "Medical History", icon: ClipboardCheck },
  { key: "safetyScreening", label: "Safety Screening", icon: AlertTriangle },
  { key: "socialFunctional", label: "Social & Functional", icon: Home },
  { key: "consents", part: 1, label: "Consents & Acknowledgments", icon: CheckCircle },
  { key: "consents", part: 2, label: "Final Acknowledgments & Signature", icon: FileSignature },
];

const INITIAL_DATA = {
  personalInfo: {}, emergencyContact: {}, insurance: {},
  referralInfo: {}, presentingConcerns: {}, medicalHistory: {},
  safetyScreening: {}, socialFunctional: {}, consents: {},
};

const Field = ({ label, name, value, onChange, type = "text", required, options, placeholder, rows, onBlur, colorCode }) => {
  const id = `intake-${name}`;
  if (type === "select") {
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <select id={id} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
          data-testid={id}>
          <option value="">Select...</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (type === "textarea") {
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <textarea id={id} value={value || ""} onChange={(e) => onChange(e.target.value)}
          rows={rows || 3} placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          data-testid={id} />
      </div>
    );
  }
  if (type === "checkbox") {
    return (
      <label className="flex items-start gap-3 cursor-pointer py-1">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          data-testid={id} />
        <span className="text-sm text-gray-700">{label} {required && <span className="text-red-500">*</span>}</span>
      </label>
    );
  }
  if (type === "yesno") {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="flex gap-3">
          {["Yes", "No"].map((opt) => {
            const selected = value === opt;
            let selectedClasses = "bg-blue-50 border-blue-400 text-blue-700";
            if (colorCode && selected) {
              selectedClasses = opt === "Yes"
                ? "bg-green-50 border-green-500 text-green-700 font-semibold"
                : "bg-red-50 border-red-500 text-red-700 font-semibold";
            }
            return (
              <button key={opt} type="button" onClick={() => onChange(opt)}
                aria-pressed={selected}
                className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-colors flex items-center gap-1.5 ${
                  selected ? selectedClasses : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
                data-testid={`${id}-${opt.toLowerCase()}`}>
                {selected && <CheckCircle className="w-3.5 h-3.5" aria-hidden="true" />}
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (type === "radio") {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="flex flex-wrap gap-3">
          {options.map((opt) => (
            <button key={opt} type="button" onClick={() => onChange(opt)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                value === opt ? "bg-blue-50 border-blue-400 text-blue-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
              data-testid={`${id}-${opt.toLowerCase().replace(/\s+/g, "-")}`}>
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (type === "tel") {
    const digits = (value || "").replace(/\D/g, "").slice(0, 10);
    const isPartial = digits.length > 0 && digits.length < 10;
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input id={id} type="tel" inputMode="numeric" value={formatPhoneDisplay(digits)}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 10))}
          onBlur={onBlur} placeholder={placeholder || "(123) 456-7890"} maxLength={14}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          data-testid={id} />
        {isPartial && <p className="text-xs text-red-500 mt-1" data-testid={`${id}-error`}>Phone number must be exactly 10 digits</p>}
      </div>
    );
  }
  if (type === "zip") {
    const digits = (value || "").replace(/\D/g, "").slice(0, 5);
    const isPartial = digits.length > 0 && digits.length < 5;
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input id={id} type="text" inputMode="numeric" value={digits}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 5))}
          onBlur={onBlur} placeholder={placeholder || "5-digit ZIP"} maxLength={5}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          data-testid={id} />
        {isPartial && <p className="text-xs text-red-500 mt-1" data-testid={`${id}-error`}>ZIP code must be exactly 5 digits</p>}
      </div>
    );
  }
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input id={id} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} onBlur={onBlur}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        data-testid={id} />
    </div>
  );
};

const US_STATES_AND_TERRITORIES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
  "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri",
  "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island",
  "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming", "District of Columbia", "American Samoa", "Guam",
  "Northern Mariana Islands", "Puerto Rico", "U.S. Virgin Islands",
];

const formatPhoneDisplay = (digits) => {
  if (!digits) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const isValidPhone = (v) => /^\d{10}$/.test((v || "").replace(/\D/g, ""));
const isValidZip = (v) => /^\d{5}$/.test((v || "").replace(/\D/g, ""));

const ConsentDisclosure = ({ title, plain, text, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-1.5">
      <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1">{title}</p>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 rounded"
        data-testid={`consent-toggle-${testId}`}
      >
        {open ? "Hide consent details" : "Read what you are consenting to"}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="p-3 mb-2 bg-blue-50 border border-blue-200 rounded-lg text-sm leading-relaxed"
          data-testid={`consent-details-${testId}`}
        >
          {plain && (
            <>
              <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wide mb-1">What This Means</p>
              <p className="text-gray-700 mb-3">{plain}</p>
            </>
          )}
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Official Acknowledgment Wording</p>
          <p className="text-gray-700">{text}</p>
        </div>
      )}
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OMHC_SERVICE_OPTIONS = [
  "Psychiatric Evaluation",
  "Medication Management",
  "Individual Therapy",
  "Family Therapy",
  "Group Therapy",
  "Diagnostic Assessment",
  "Care Coordination",
  "Other (please specify)",
];

const SSNField = ({ value, onChange, required }) => {
  const [focused, setFocused] = useState(false);
  const [masked, setMasked] = useState(true);
  const rawValue = (value || "").replace(/[^0-9]/g, "");
  const displayValue = focused || !masked || !rawValue
    ? value || ""
    : rawValue.length >= 9 ? "•••-••-" + rawValue.slice(-4) : "•".repeat(rawValue.length);

  return (
    <div>
      <label htmlFor="intake-ssn" className="block text-sm font-medium text-gray-700 mb-1">
        Social Security Number {required && <span className="text-red-500">*</span>}
      </label>
      <input
        id="intake-ssn"
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={(e) => {
          if (!focused) return;
          const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 9);
          let formatted = digits;
          if (digits.length > 5) formatted = digits.slice(0, 3) + "-" + digits.slice(3, 5) + "-" + digits.slice(5);
          else if (digits.length > 3) formatted = digits.slice(0, 3) + "-" + digits.slice(3);
          onChange(formatted);
        }}
        onFocus={() => { setFocused(true); setMasked(false); }}
        onBlur={() => { setFocused(false); setMasked(true); }}
        placeholder="XXX-XX-XXXX"
        autoComplete="off"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        data-testid="intake-ssn"
      />
      {!focused && rawValue.length > 0 && rawValue.length < 9 && (
        <p className="text-xs text-red-500 mt-1">SSN must be 9 digits</p>
      )}
    </div>
  );
};

const OMHCServicesField = ({ selected, onChange, otherText, onOtherChange, required }) => {
  const toggleService = (svc) => {
    const current = selected || [];
    if (current.includes(svc)) {
      onChange(current.filter((s) => s !== svc));
    } else {
      onChange([...current, svc]);
    }
  };
  const hasOther = (selected || []).includes("Other (please specify)");

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        OMHC Requested Services {required && <span className="text-red-500">*</span>}
      </label>
      <p className="text-xs text-gray-500 mb-3">Select at least one service.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {OMHC_SERVICE_OPTIONS.map((svc) => (
          <label key={svc} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border border-gray-200 hover:bg-blue-50 transition-colors">
            <input
              type="checkbox"
              checked={(selected || []).includes(svc)}
              onChange={() => toggleService(svc)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              data-testid={`omhc-svc-${svc.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            />
            <span className="text-sm text-gray-700">{svc}</span>
          </label>
        ))}
      </div>
      {hasOther && (
        <div className="mt-3">
          <Field
            label="Please specify other service(s)"
            name="omhcOtherSpecify"
            value={otherText}
            onChange={onOtherChange}
            required
            placeholder="Describe the service(s) you are requesting"
          />
        </div>
      )}
    </div>
  );
};

const CardUpload = ({ label, fileId, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please select an image file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_URL}/api/intake/upload-card`, true);
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const data = JSON.parse(xhr.responseText);
          onUploaded(data.file_id);
          setFileName(file.name);
        } else { alert("Upload failed"); }
        setUploading(false);
      };
      xhr.onerror = () => { alert("Upload failed"); setUploading(false); };
      xhr.send(formData);
    } catch { alert("Upload failed"); setUploading(false); }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {fileId ? (
        <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          <span className="text-xs text-green-700 truncate">{fileName || "Uploaded"}</span>
          <button type="button" onClick={() => { onUploaded(""); setFileName(""); }} className="text-xs text-red-500 hover:text-red-700 ml-auto">Remove</button>
        </div>
      ) : (
        <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploading ? "border-blue-300 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"}`}>
          {uploading ? (
            <><Loader2 className="w-4 h-4 text-blue-500 animate-spin" /><span className="text-xs text-blue-600">Uploading...</span></>
          ) : (
            <><span className="text-xs text-gray-500">Click to upload {label.toLowerCase()}</span></>
          )}
          <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} data-testid={`upload-${label.toLowerCase().replace(/\s+/g, "-")}`} />
        </label>
      )}
    </div>
  );
};

const STORAGE_KEY = "sdora_intake_draft";

const IntakePage = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_DATA);
  const [draftToken, setDraftToken] = useState(searchParams.get("resume") || "");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [safetyFlag, setSafetyFlag] = useState(false);
  const [errors, setErrors] = useState([]);
  const [restored, setRestored] = useState(false);

  // Restore from localStorage on mount (only if no server draft token)
  useEffect(() => {
    const resumeToken = searchParams.get("resume");
    if (resumeToken) {
      safeJsonRequest(`/api/intake/resume/${resumeToken}`).then((d) => {
        setFormData((prev) => ({ ...prev, ...d.form_data }));
        setDraftToken(d.draft_token);
        setRestored(true);
      }).catch(() => { restoreFromLocal(); });
    } else {
      restoreFromLocal();
    }
  }, [searchParams]);

  const restoreFromLocal = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.formData && Object.keys(parsed.formData.personalInfo || {}).length > 0) {
          setFormData((prev) => ({ ...prev, ...parsed.formData }));
          if (typeof parsed.step === "number") setStep(parsed.step);
          setRestored(true);
        }
      }
    } catch { /* ignore corrupted data */ }
  };

  // Auto-save to localStorage on every change (debounced via effect)
  useEffect(() => {
    if (submitted) return;
    const timeout = setTimeout(() => {
      try {
        // Never store raw SSN in localStorage — strip it before saving
        const safeData = JSON.parse(JSON.stringify(formData));
        if (safeData.personalInfo?.ssn) {
          safeData.personalInfo.ssn = "";
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ formData: safeData, step }));
      } catch { /* ignore */ }
    }, 500);
    return () => clearTimeout(timeout);
  }, [formData, step, submitted]);

  // Clear localStorage on successful submission
  const clearLocalDraft = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  // Default Consent Effective Date to today once the client reaches the final signature step
  useEffect(() => {
    const part2Index = SECTIONS.findIndex((s) => s.key === "consents" && s.part === 2);
    if (step === part2Index && !formData.consents?.consentEffectiveDate) {
      const todayISO = new Date().toISOString().slice(0, 10);
      setFormData((prev) => ({ ...prev, consents: { ...prev.consents, consentEffectiveDate: todayISO } }));
    }
  }, [step, formData.consents]);

  const updateField = useCallback((section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  }, []);

  const saveDraft = async () => {
    setSaving(true);
    try {
      const res = await safeJsonRequest("/api/intake/save-draft", {
        method: "POST", body: { form_data: formData, draft_token: draftToken, is_draft: true },
      });
      setDraftToken(res.draft_token);
      alert("Draft saved! You can resume later using this link:\n" + window.location.origin + "/intake?resume=" + res.draft_token);
    } catch { alert("Failed to save draft"); }
    finally { setSaving(false); }
  };

  const validateStep = (stepIdx) => {
    const sec = SECTIONS[stepIdx];
    const d = formData[sec.key] || {};
    const errs = [];
    if (sec.key === "personalInfo") {
      if (!d.legalFirstName) errs.push("First name is required");
      if (!d.legalLastName) errs.push("Last name is required");
      if (!d.dob) errs.push("Date of birth is required");
      if (!d.sexAtBirth) errs.push("Sex at birth is required");
      if (!d.addressLine1) errs.push("Address is required");
      if (!d.city) errs.push("City is required");
      if (!d.state) errs.push("State is required");
      if (!d.zip) errs.push("ZIP code is required");
      else if (!isValidZip(d.zip)) errs.push("ZIP code must be exactly 5 digits");
      if (!d.phone) errs.push("Phone is required");
      else if (!isValidPhone(d.phone)) errs.push("Phone number must be exactly 10 digits");
      if (d.guardianPhone && !isValidPhone(d.guardianPhone)) errs.push("Guardian phone must be exactly 10 digits");
      if (!d.preferredContact) errs.push("Preferred contact method is required");
      if (!d.languagePreference) errs.push("Language preference is required");
      // SSN mandatory
      const rawSSN = (d.ssn || "").replace(/[^0-9]/g, "");
      if (rawSSN.length < 9) errs.push("A valid 9-digit SSN is required");
    } else if (sec.key === "emergencyContact") {
      if (!d.ecName) errs.push("Emergency contact name is required");
      if (!d.ecRelationship) errs.push("Relationship is required");
      if (!d.ecPhone) errs.push("Phone is required");
      else if (!isValidPhone(d.ecPhone)) errs.push("Phone number must be exactly 10 digits");
      if (d.ecAltPhone && !isValidPhone(d.ecAltPhone)) errs.push("Alternate phone must be exactly 10 digits");
      if (!d.ecPermissionToContact) errs.push("Permission to contact is required");
    } else if (sec.key === "insurance") {
      if (!d.insuranceCarrier) errs.push("Insurance carrier is required");
      if (!d.memberId) errs.push("Member ID is required");
      if (!d.policyholderName) errs.push("Policyholder name is required");
      if (!d.policyholderRelationship) errs.push("Policyholder relationship is required");
    } else if (sec.key === "referralInfo") {
      if (!d.requestedService) errs.push("Requested service is required");
      if (!d.omhcRequestedServices || d.omhcRequestedServices.length === 0) errs.push("Please select at least one OMHC requested service.");
      if ((d.omhcRequestedServices || []).includes("Other (please specify)") && !d.omhcOtherSpecify?.trim()) {
        errs.push("Please specify the 'Other' service you are requesting.");
      }
      if (!d.reasonForReferral) errs.push("Reason for seeking services is required");
    } else if (sec.key === "presentingConcerns") {
      if (!d.primaryConcerns) errs.push("Primary concerns are required");
    } else if (sec.key === "medicalHistory") {
      if (!d.currentDiagnoses) errs.push("Current diagnoses are required (enter 'None' if not applicable)");
    } else if (sec.key === "safetyScreening") {
      if (!d.suicidalThoughts) errs.push("All safety questions must be answered");
      if (!d.selfHarm) errs.push("All safety questions must be answered");
      if (!d.harmToOthers) errs.push("All safety questions must be answered");
      if (!d.recentCrisis) errs.push("All safety questions must be answered");
      if (!d.immediateSafetyRisk) errs.push("All safety questions must be answered");
    } else if (sec.key === "consents" && sec.part === 1) {
      const isMinor = (formData.personalInfo || {}).isMinor === "Yes";
      const prpSelected = (formData.referralInfo || {}).requestedService === "PRP — Psychiatric Rehabilitation";
      if (isMinor && !d.guardianType) errs.push("Please select who is signing on behalf of the minor client (parent, legal guardian, or court-appointed guardian)");
      if (d.legalRepPhone && !isValidPhone(d.legalRepPhone)) errs.push("Legal representative phone must be exactly 10 digits");
      if (d.consentToTreatment !== "Yes") errs.push("Consent to treatment is required");
      if (!d.privacyPractices) errs.push("Privacy practices acknowledgment is required");
      if (d.electronicCommunication !== "Yes") errs.push("Electronic communication consent is required");
      if (d.telehealthConsent !== "Yes") errs.push("Please acknowledge the telehealth services policy");
      if (d.emergencyContactAuth !== "Yes") errs.push("Emergency contact authorization is required");
      if (!d.financialResponsibility) errs.push("Financial responsibility acknowledgment is required");
      if (!d.grievanceProcedure) errs.push("Please acknowledge the Grievance & Complaint Procedure");
      if (!d.clientRights) errs.push("Please acknowledge the Client Rights & Responsibilities");
      if (!d.serviceDeliveryPreference) errs.push("Please select a service delivery preference (onsite, offsite, or blended)");
      if (!d.serviceDeliveryPreferenceInitials || !d.serviceDeliveryPreferenceInitials.trim()) errs.push("Please initial next to your service delivery preference choice");
      if (prpSelected) {
        if (!d.prpAcknowledgment) errs.push("Please acknowledge the Psychiatric Rehabilitation Services explanation");
        if (!d.prpAcknowledgmentInitials || !d.prpAcknowledgmentInitials.trim()) errs.push("Please initial the Psychiatric Rehabilitation Services acknowledgment");
      }
    } else if (sec.key === "consents" && sec.part === 2) {
      if (!d.transportationConsent) errs.push("Please answer the transportation authorization question");
      if (!d.transportationConsentInitials || !d.transportationConsentInitials.trim()) errs.push("Please initial the transportation authorization");
      if (!d.rightToRefuse) errs.push("Please acknowledge your right to refuse treatment");
      if (!d.consentEffectiveDate) errs.push("Consent effective date is required");
      const todayISO = new Date().toISOString().slice(0, 10);
      if (d.consentEffectiveDate && d.consentEffectiveDate < todayISO && !(d.effectiveDateReason || "").trim()) {
        errs.push("Please document why the consent effective date differs from today's electronic signature date");
      }
      if (!d.signatureText) errs.push("Electronic signature is required");
      if (!d.signaturePrintedName) errs.push("Printed name is required");
      if (!d.signatureDate) errs.push("Date is required");
    }
    return [...new Set(errs)];
  };

  const handleNext = () => {
    const errs = validateStep(step);
    if (errs.length > 0) { setErrors(errs); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setErrors([]);
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    // Full cross-step validation before submission
    for (let i = 0; i < SECTIONS.length; i++) {
      const stepErrs = validateStep(i);
      if (stepErrs.length > 0) {
        setErrors(stepErrs);
        setStep(i);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }
    setErrors([]);
    setSubmitting(true);
    try {
      const res = await safeJsonRequest("/api/intake/submit", {
        method: "POST", body: { form_data: formData, draft_token: draftToken },
      });
      setSafetyFlag(res.safety_flag);
      setSubmitted(true);
      clearLocalDraft();
    } catch (err) {
      if (err.detail?.errors) {
        setErrors(err.detail.errors.map((e) => e.split(": ").pop() || e));
      } else {
        alert("Submission failed. Please try again.");
      }
    }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="Intake Submitted" path="/intake" />
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Intake Form Submitted</h1>
          <p className="text-gray-600 mb-2">Your intake paperwork has been received. Our team will review it and contact you to schedule your first appointment.</p>
          {safetyFlag && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-left">
              <p className="text-sm text-red-700 font-semibold mb-1">Important Safety Notice</p>
              <p className="text-sm text-red-600">Based on your responses, a member of our clinical team will reach out to you promptly. If you are in immediate danger, please call <strong>911</strong> or the <strong>988 Suicide & Crisis Lifeline</strong>.</p>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-6">Questions? Call us at <a href="tel:+18777725150" className="text-blue-600 font-semibold">(877) 772-5150</a></p>
        </div>
        <Footer />
      </div>
    );
  }

  const sec = SECTIONS[step];
  const sectionData = formData[sec.key] || {};
  const u = (field, val) => updateField(sec.key, field, val);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Client Intake Form" description="Complete your intake paperwork online for SDORA Wellbeing mental health services." path="/intake" />
      <h1 className="sr-only">Client Intake & Consent Form</h1>
      <Navigation />

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Printable packet option + paper upload */}
        {step === 0 && (
          <div className="mb-4 space-y-2">
            <div className="p-3 bg-gray-100 border border-gray-200 rounded-xl flex items-center justify-between" data-testid="printable-packet-banner">
              <p className="text-xs text-gray-600">Prefer paper? <strong>Download our printable intake packet</strong> and bring it to your appointment.</p>
              <button
                onClick={downloadBlankIntakePacket}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 whitespace-nowrap ml-3"
                data-testid="download-packet-btn"
              >
                <FileText className="w-3.5 h-3.5" /> Download PDF
              </button>
            </div>
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-xl" data-testid="paper-upload-section">
              <p className="text-xs text-teal-700 mb-2">Already completed a paper form? Upload it securely.</p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 20 * 1024 * 1024) { alert("File exceeds 20MB limit"); return; }
                    setUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append("file", f);
                      const resp = await fetch(`${API_URL}/api/intake/upload-paper-packet`, { method: "POST", body: fd });
                      if (!resp.ok) throw new Error("Upload failed");
                      const result = await resp.json();
                      setUploadSuccess(result.id);
                    } catch { alert("Upload failed. Please try again."); }
                    finally { setUploading(false); }
                  }}
                  data-testid="paper-upload-input"
                />
                <span className="text-xs text-teal-700 font-semibold hover:text-teal-900 flex items-center gap-1">
                  <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading..." : "Choose File (PDF, JPEG, PNG)"}
                </span>
              </label>
              {uploadSuccess && (
                <p className="text-xs text-green-700 mt-2 font-medium" data-testid="paper-upload-success">
                  Paper packet uploaded successfully. Our team will review it shortly.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">Step {step + 1} of {SECTIONS.length}</span>
            <button onClick={saveDraft} disabled={saving} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium" data-testid="save-draft-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save & Resume Later
            </button>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-300 rounded-full" style={{ width: `${((step + 1) / SECTIONS.length) * 100}%` }} />
          </div>
        </div>

        {/* Restored from draft banner */}
        {restored && step === 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between" data-testid="draft-restored-banner">
            <p className="text-xs text-blue-700">Your previous progress has been restored.</p>
            <button onClick={() => { setFormData(INITIAL_DATA); setStep(0); clearLocalDraft(); setRestored(false); }} className="text-xs text-blue-500 hover:text-blue-700 font-medium" data-testid="clear-draft-btn">Start Fresh</button>
          </div>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="validation-errors" role="alert" aria-live="polite">
            <p className="text-sm font-semibold text-red-700 mb-1">Please fix the following:</p>
            {errors.map((e, i) => <p key={i} className="text-sm text-red-600">- {e}</p>)}
          </div>
        )}

        {/* Section Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <sec.icon className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">{sec.label}</h2>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Section 1: Personal Info */}
            {sec.key === "personalInfo" && (<>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Legal First Name" name="legalFirstName" value={sectionData.legalFirstName} onChange={(v) => u("legalFirstName", v)} required />
                <Field label="Middle Name" name="legalMiddleName" value={sectionData.legalMiddleName} onChange={(v) => u("legalMiddleName", v)} />
                <Field label="Legal Last Name" name="legalLastName" value={sectionData.legalLastName} onChange={(v) => u("legalLastName", v)} required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Preferred Name" name="preferredName" value={sectionData.preferredName} onChange={(v) => u("preferredName", v)} />
                <Field label="Date of Birth" name="dob" type="date" value={sectionData.dob} onChange={(v) => u("dob", v)} required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Sex at Birth" name="sexAtBirth" type="select" value={sectionData.sexAtBirth} onChange={(v) => u("sexAtBirth", v)} required options={["Male", "Female", "Intersex", "Prefer not to say"]} />
                <Field label="Gender Identity" name="genderIdentity" type="select" value={sectionData.genderIdentity} onChange={(v) => u("genderIdentity", v)} options={["Man", "Woman", "Non-binary", "Transgender", "Other", "Prefer not to say"]} />
              </div>
              <Field label="Pronouns" name="pronouns" value={sectionData.pronouns} onChange={(v) => u("pronouns", v)} placeholder="e.g. he/him, she/her, they/them" />
              <Field label="Street Address" name="addressLine1" value={sectionData.addressLine1} onChange={(v) => u("addressLine1", v)} required />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="City" name="city" value={sectionData.city} onChange={(v) => u("city", v)} required />
                <Field label="State" name="state" type="select" options={US_STATES_AND_TERRITORIES} value={sectionData.state} onChange={(v) => u("state", v)} required />
                <Field label="ZIP" name="zip" type="zip" value={sectionData.zip} onChange={(v) => u("zip", v)} required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Phone" name="phone" type="tel" value={sectionData.phone} onChange={(v) => u("phone", v)} required />
                <Field label="Email" name="email" type="email" value={sectionData.email} onChange={(v) => u("email", v)} />
              </div>
              <Field label="Preferred Contact Method" name="preferredContact" type="select" value={sectionData.preferredContact} onChange={(v) => u("preferredContact", v)} required options={["Phone", "Email", "Text", "No preference"]} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Marital Status" name="maritalStatus" type="select" value={sectionData.maritalStatus} onChange={(v) => u("maritalStatus", v)} options={["Single", "Married", "Divorced", "Separated", "Widowed", "Domestic Partnership", "Prefer not to say"]} />
                <Field label="Race" name="race" value={sectionData.race} onChange={(v) => u("race", v)} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Ethnicity" name="ethnicity" value={sectionData.ethnicity} onChange={(v) => u("ethnicity", v)} />
                <Field label="Language Preference" name="languagePreference" value={sectionData.languagePreference} onChange={(v) => u("languagePreference", v)} required placeholder="e.g. English, Spanish" />
              </div>
              <SSNField value={sectionData.ssn} onChange={(v) => u("ssn", v)} required />
              <Field label="Is the client a minor?" name="isMinor" type="yesno" colorCode value={sectionData.isMinor} onChange={(v) => u("isMinor", v)} />
              {sectionData.isMinor === "Yes" && (<div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 bg-blue-50 rounded-lg">
                <Field label="Guardian Name" name="guardianName" value={sectionData.guardianName} onChange={(v) => u("guardianName", v)} />
                <Field label="Guardian Phone" name="guardianPhone" type="tel" value={sectionData.guardianPhone} onChange={(v) => u("guardianPhone", v)} />
                <Field label="Relationship" name="guardianRelationship" value={sectionData.guardianRelationship} onChange={(v) => u("guardianRelationship", v)} />
              </div>)}
            </>)}

            {/* Section 2: Emergency Contact */}
            {sec.key === "emergencyContact" && (<>
              <Field label="Emergency Contact Name" name="ecName" value={sectionData.ecName} onChange={(v) => u("ecName", v)} required />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Relationship" name="ecRelationship" value={sectionData.ecRelationship} onChange={(v) => u("ecRelationship", v)} required />
                <Field label="Phone" name="ecPhone" type="tel" value={sectionData.ecPhone} onChange={(v) => u("ecPhone", v)} required />
              </div>
              <Field label="Alternate Phone" name="ecAltPhone" type="tel" value={sectionData.ecAltPhone} onChange={(v) => u("ecAltPhone", v)} />
              <Field label="Permission to contact this person?" name="ecPermissionToContact" type="yesno" colorCode value={sectionData.ecPermissionToContact} onChange={(v) => u("ecPermissionToContact", v)} required />
            </>)}

            {/* Section 3: Insurance */}
            {sec.key === "insurance" && (<>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Insurance Carrier / MCO" name="insuranceCarrier" value={sectionData.insuranceCarrier} onChange={(v) => u("insuranceCarrier", v)} required />
                <Field label="Member ID" name="memberId" value={sectionData.memberId} onChange={(v) => u("memberId", v)} required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Group Number" name="groupNumber" value={sectionData.groupNumber} onChange={(v) => u("groupNumber", v)} />
                <Field label="Policyholder Name" name="policyholderName" value={sectionData.policyholderName} onChange={(v) => u("policyholderName", v)} required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Policyholder DOB" name="policyholderDob" type="date" value={sectionData.policyholderDob} onChange={(v) => u("policyholderDob", v)} />
                <Field label="Relationship to Policyholder" name="policyholderRelationship" type="select" value={sectionData.policyholderRelationship} onChange={(v) => u("policyholderRelationship", v)} required options={["Self", "Spouse", "Parent", "Child", "Other"]} />
              </div>
              <Field label="Do you have secondary insurance?" name="hasSecondaryInsurance" type="yesno" value={sectionData.hasSecondaryInsurance} onChange={(v) => u("hasSecondaryInsurance", v)} />
              {sectionData.hasSecondaryInsurance === "Yes" && (<div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg">
                <Field label="Secondary Carrier" name="secondaryCarrier" value={sectionData.secondaryCarrier} onChange={(v) => u("secondaryCarrier", v)} />
                <Field label="Secondary Member ID" name="secondaryMemberId" value={sectionData.secondaryMemberId} onChange={(v) => u("secondaryMemberId", v)} />
              </div>)}

              {/* Insurance Card Upload */}
              <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-sm font-bold text-gray-800 mb-3">Insurance Card Images (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CardUpload label="Card Front" fileId={sectionData.cardFrontId} onUploaded={(id) => u("cardFrontId", id)} />
                  <CardUpload label="Card Back" fileId={sectionData.cardBackId} onUploaded={(id) => u("cardBackId", id)} />
                </div>
              </div>
            </>)}

            {/* Section 4: Referral/Service Info */}
            {sec.key === "referralInfo" && (<>
              <Field label="How did you hear about SDORA?" name="howHeard" value={sectionData.howHeard} onChange={(v) => u("howHeard", v)} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Referring Provider" name="referringProvider" value={sectionData.referringProvider} onChange={(v) => u("referringProvider", v)} />
                <Field label="Referring Organization" name="referringOrganization" value={sectionData.referringOrganization} onChange={(v) => u("referringOrganization", v)} />
              </div>
              <Field label="Requested Service" name="requestedService" type="select" value={sectionData.requestedService} onChange={(v) => u("requestedService", v)} required options={["OMHC — Outpatient Therapy", "PRP — Psychiatric Rehabilitation", "Medication Management", "Individual Therapy", "Family Therapy", "Case Management", "Comprehensive Assessment", "Other"]} />
              <OMHCServicesField
                selected={sectionData.omhcRequestedServices}
                onChange={(v) => u("omhcRequestedServices", v)}
                otherText={sectionData.omhcOtherSpecify}
                onOtherChange={(v) => u("omhcOtherSpecify", v)}
                required
              />
              <Field label="Reason for Seeking Services" name="reasonForReferral" type="textarea" value={sectionData.reasonForReferral} onChange={(v) => u("reasonForReferral", v)} required placeholder="Briefly describe the reason for seeking services..." />
              <Field label="Preferred Service Format" name="preferredFormat" type="select" value={sectionData.preferredFormat} onChange={(v) => u("preferredFormat", v)} options={["In-person", "Telehealth", "No preference"]} />
            </>)}

            {/* Section 5: Presenting Concerns */}
            {sec.key === "presentingConcerns" && (<>
              <Field label="Primary Concerns" name="primaryConcerns" type="textarea" value={sectionData.primaryConcerns} onChange={(v) => u("primaryConcerns", v)} required placeholder="What are your main concerns?" />
              <Field label="Symptoms / Challenges" name="symptoms" type="textarea" value={sectionData.symptoms} onChange={(v) => u("symptoms", v)} placeholder="Describe any symptoms or daily challenges..." />
              <Field label="When did these concerns begin?" name="onsetDate" value={sectionData.onsetDate} onChange={(v) => u("onsetDate", v)} placeholder="e.g. 6 months ago, childhood, after an event" />
              <Field label="Current Level of Functioning" name="functioningLevel" type="select" value={sectionData.functioningLevel} onChange={(v) => u("functioningLevel", v)} options={["High — managing daily tasks well", "Moderate — some difficulty with daily tasks", "Low — significant difficulty functioning", "Crisis — unable to manage daily life"]} />
              <Field label="Treatment Goals" name="treatmentGoals" type="textarea" value={sectionData.treatmentGoals} onChange={(v) => u("treatmentGoals", v)} placeholder="What do you hope to achieve through treatment?" />
              <Field label="Previous Behavioral Health Treatment" name="previousTreatment" type="textarea" value={sectionData.previousTreatment} onChange={(v) => u("previousTreatment", v)} placeholder="Describe any previous therapy, counseling, or psychiatric care" />
              <Field label="Previous Psychiatric Hospitalizations" name="previousHospitalizations" type="textarea" value={sectionData.previousHospitalizations} onChange={(v) => u("previousHospitalizations", v)} />
            </>)}

            {/* Section 6: Medical History */}
            {sec.key === "medicalHistory" && (<>
              <Field label="Primary Care Provider" name="primaryCareProvider" value={sectionData.primaryCareProvider} onChange={(v) => u("primaryCareProvider", v)} placeholder="Name and/or practice" />
              <Field label="Current Diagnoses" name="currentDiagnoses" type="textarea" value={sectionData.currentDiagnoses} onChange={(v) => u("currentDiagnoses", v)} required placeholder="List any current mental health or medical diagnoses (enter 'None' if not applicable)" />
              <Field label="Medical Conditions" name="medicalConditions" type="textarea" value={sectionData.medicalConditions} onChange={(v) => u("medicalConditions", v)} placeholder="e.g. diabetes, asthma, chronic pain" />
              <Field label="Allergies" name="allergies" value={sectionData.allergies} onChange={(v) => u("allergies", v)} placeholder="Medication or other allergies" />
              <Field label="Current Medications" name="currentMedications" type="textarea" value={sectionData.currentMedications} onChange={(v) => u("currentMedications", v)} placeholder="Name, dose, and frequency if known" />
              <Field label="Current Behavioral Health Providers" name="currentBHProviders" value={sectionData.currentBHProviders} onChange={(v) => u("currentBHProviders", v)} />
              <Field label="Substance Use History" name="substanceUseHistory" type="textarea" value={sectionData.substanceUseHistory} onChange={(v) => u("substanceUseHistory", v)} placeholder="If applicable, describe current or past substance use" />
              <Field label="Relevant Hospitalization History" name="hospitalizationHistory" type="textarea" value={sectionData.hospitalizationHistory} onChange={(v) => u("hospitalizationHistory", v)} />
            </>)}

            {/* Section 7: Safety Screening */}
            {sec.key === "safetyScreening" && (<>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-2">
                <p className="text-sm text-amber-800">These questions help our team ensure your safety. If you are in immediate danger, call <strong>911</strong> or the <strong>988 Suicide & Crisis Lifeline</strong>.</p>
              </div>
              <Field label="Are you currently having thoughts of suicide?" name="suicidalThoughts" type="yesno" colorCode value={sectionData.suicidalThoughts} onChange={(v) => u("suicidalThoughts", v)} required />
              <Field label="Are you currently engaging in self-harm?" name="selfHarm" type="yesno" colorCode value={sectionData.selfHarm} onChange={(v) => u("selfHarm", v)} required />
              <Field label="Are you having thoughts of harming others?" name="harmToOthers" type="yesno" colorCode value={sectionData.harmToOthers} onChange={(v) => u("harmToOthers", v)} required />
              <Field label="Have you had a recent psychiatric crisis?" name="recentCrisis" type="yesno" colorCode value={sectionData.recentCrisis} onChange={(v) => u("recentCrisis", v)} required />
              <Field label="Do you feel you are at immediate safety risk?" name="immediateSafetyRisk" type="yesno" colorCode value={sectionData.immediateSafetyRisk} onChange={(v) => u("immediateSafetyRisk", v)} required />
              {["suicidalThoughts", "selfHarm", "harmToOthers", "recentCrisis", "immediateSafetyRisk"].some((k) => sectionData[k] === "Yes") && (
                <div className="p-4 bg-red-50 border-2 border-red-400 rounded-lg flex items-start gap-3 shadow-sm" role="alert" data-testid="safety-escalation-alert">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-bold text-red-800">Safety Concern Identified</p>
                    <p className="text-sm text-red-700 mt-1">
                      One or more of your answers above indicates a safety concern. Our clinical team will follow up with you about this as part of your intake review — you do not need to stop and may continue completing this form.
                    </p>
                    <p className="text-sm text-red-700 mt-2">
                      If you are in immediate danger right now, please call <a href="tel:911" className="font-bold underline hover:text-red-900">911</a> or the <a href="tel:988" className="font-bold underline hover:text-red-900">988 Suicide &amp; Crisis Lifeline</a> (call or text) right away.
                    </p>
                  </div>
                </div>
              )}
              <Field label="Additional safety details (optional)" name="safetyDetails" type="textarea" value={sectionData.safetyDetails} onChange={(v) => u("safetyDetails", v)} placeholder="Provide any details that may help our team support your safety" />
            </>)}

            {/* Section 8: Social/Functional */}
            {sec.key === "socialFunctional" && (<>
              <Field label="Housing Status" name="housingStatus" type="select" value={sectionData.housingStatus} onChange={(v) => u("housingStatus", v)} options={["Stable housing", "Temporary housing", "Homeless", "Shelter", "Group home", "Other"]} />
              <Field label="Employment / School Status" name="employmentStatus" type="select" value={sectionData.employmentStatus} onChange={(v) => u("employmentStatus", v)} options={["Employed full-time", "Employed part-time", "Unemployed", "Student", "Retired", "Disabled", "Other"]} />
              <Field label="Transportation Needs" name="transportationNeeds" type="select" value={sectionData.transportationNeeds} onChange={(v) => u("transportationNeeds", v)} options={["Have own transportation", "Need transportation assistance", "Use public transit", "Other"]} />
              <Field label="Legal/Court Involvement" name="legalInvolvement" value={sectionData.legalInvolvement} onChange={(v) => u("legalInvolvement", v)} placeholder="If applicable" />
              <Field label="DJS Involvement" name="djsInvolvement" value={sectionData.djsInvolvement} onChange={(v) => u("djsInvolvement", v)} placeholder="If applicable" />
              <Field label="Social/Support System" name="supportSystem" type="textarea" value={sectionData.supportSystem} onChange={(v) => u("supportSystem", v)} placeholder="Family, friends, community supports..." />
              <Field label="Accessibility or Accommodation Needs" name="accessibilityNeeds" value={sectionData.accessibilityNeeds} onChange={(v) => u("accessibilityNeeds", v)} />
            </>)}

            {/* Section 9 Part 1: Consents & Acknowledgments (Items 1-10) */}
            {sec.key === "consents" && sec.part === 1 && (<>
              {(() => {
                const isMinor = (formData.personalInfo || {}).isMinor === "Yes";
                const prpSelected = (formData.referralInfo || {}).requestedService === "PRP — Psychiatric Rehabilitation";
                return (<>
                  {isMinor ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="intake-guardian-signer-block">
                      <h3 className="text-sm font-bold text-gray-800 mb-2">Who Is Signing for This Minor Client?</h3>
                      <Field label="Signer Role" name="guardianType" type="radio" value={sectionData.guardianType} onChange={(v) => u("guardianType", v)} required options={["Parent", "Legal Guardian", "Court-Appointed Guardian"]} />
                      {sectionData.guardianType === "Court-Appointed Guardian" && (
                        <p className="text-xs text-amber-700 mt-2" data-testid="intake-court-guardian-note">
                          Documentation of court-appointed guardianship will be requested and verified prior to the client's first appointment.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg" data-testid="intake-legal-rep-block">
                      <Field label="Does the client have a legal guardian, representative payee, or Power of Attorney for healthcare decisions?" name="hasLegalRepresentative" type="yesno" value={sectionData.hasLegalRepresentative} onChange={(v) => u("hasLegalRepresentative", v)} />
                      {sectionData.hasLegalRepresentative === "Yes" && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                          <Field label="Representative Name" name="legalRepName" value={sectionData.legalRepName} onChange={(v) => u("legalRepName", v)} />
                          <Field label="Relationship" name="legalRepRelationship" value={sectionData.legalRepRelationship} onChange={(v) => u("legalRepRelationship", v)} />
                          <Field label="Phone" name="legalRepPhone" type="tel" value={sectionData.legalRepPhone} onChange={(v) => u("legalRepPhone", v)} />
                        </div>
                      )}
                    </div>
                  )}
                  <div className="space-y-4 mt-4">
                    <div>
                      <ConsentDisclosure title="Item 1 — Consent to Treatment" plain="This means you are agreeing to receive mental health or psychiatric rehabilitation services from SDORA Wellbeing. It applies to OMHC (outpatient therapy and medication management) and/or PRP (psychiatric rehabilitation) services, whichever you are requesting. If the client is a minor, this is signed by the parent, legal guardian, or court-appointed guardian; an authorized representative or agency representative/custodian may sign where applicable." text="I consent to receive treatment and services from SDORA Wellbeing LLC." testId="consentToTreatment" />
                      <Field label="Item 1 — I consent to receive treatment and services from SDORA Wellbeing LLC." name="consentToTreatment" type="yesno" colorCode value={sectionData.consentToTreatment} onChange={(v) => u("consentToTreatment", v)} required />
                    </div>
                    <div>
                      <ConsentDisclosure title="Item 2 — Notice of Privacy Practices" plain="This confirms you were given SDORA's Notice of Privacy Practices, a document that explains how your health information (OMHC and PRP records alike) is protected and used under HIPAA, and what rights you have regarding that information." text="I acknowledge I have received and reviewed the Notice of Privacy Practices." testId="privacyPractices" />
                      <Field label="Item 2 — I acknowledge I have received and reviewed the Notice of Privacy Practices." name="privacyPractices" type="checkbox" value={sectionData.privacyPractices} onChange={(v) => u("privacyPractices", v)} required />
                    </div>
                    <div>
                      <ConsentDisclosure title="Item 3 — Electronic Communication Consent" plain="This allows SDORA to reach you by email, text message, or through the client portal for things like appointment reminders and forms. It does not replace emergency communication, and you can ask to change your preferred contact method at any time." text="I consent to communication via electronic means (email, text, patient portal)." testId="electronicCommunication" />
                      <Field label="Item 3 — I consent to communication via electronic means (email, text, patient portal)." name="electronicCommunication" type="yesno" colorCode value={sectionData.electronicCommunication} onChange={(v) => u("electronicCommunication", v)} required />
                    </div>
                    <div>
                      <ConsentDisclosure
                        title="Item 4 — Telehealth Services Policy"
                        plain={prpSelected
                          ? "This explains SDORA's telehealth policy for PRP services specifically: Maryland COMAR regulations limit telehealth use for PRP, so an in-person assessment is required first, and telehealth sessions are only used afterward when your treatment team determines it is clinically appropriate."
                          : "This explains SDORA's telehealth policy for OMHC (outpatient therapy/medication) services: secure video or phone sessions can be used in place of in-person visits whenever your clinician determines it is appropriate for your care."}
                        text={prpSelected
                          ? "I understand that Psychiatric Rehabilitation Program (PRP) services delivered via telehealth are limited under Maryland COMAR regulations. An initial in-person assessment is required, and telehealth will only be used for PRP sessions when clinically appropriate and approved by my treatment team."
                          : "I consent to receive outpatient mental health services via telehealth (secure video or telephone) when clinically appropriate for my care."}
                        testId="telehealthConsent"
                      />
                      <Field
                        label={prpSelected
                          ? "Item 4 — I understand that Psychiatric Rehabilitation Program (PRP) services delivered via telehealth are limited under Maryland COMAR regulations. An initial in-person assessment is required, and telehealth will only be used for PRP sessions when clinically appropriate and approved by my treatment team."
                          : "Item 4 — I consent to receive outpatient mental health services via telehealth (secure video or telephone) when clinically appropriate for my care."}
                        name="telehealthConsent" type="yesno" colorCode value={sectionData.telehealthConsent} onChange={(v) => u("telehealthConsent", v)} required
                      />
                    </div>
                    <div>
                      <ConsentDisclosure title="Item 5 — Emergency Contact Authorization" plain="This allows SDORA staff to reach out to the emergency contact you listed if there is ever a concern about your safety or wellbeing. It applies regardless of whether you are receiving OMHC or PRP services." text="I authorize SDORA Wellbeing to contact my emergency contact when necessary for my safety." testId="emergencyContactAuth" />
                      <Field label="Item 5 — I authorize SDORA Wellbeing to contact my emergency contact when necessary for my safety." name="emergencyContactAuth" type="yesno" colorCode value={sectionData.emergencyContactAuth} onChange={(v) => u("emergencyContactAuth", v)} required />
                    </div>
                    <div>
                      <ConsentDisclosure title="Item 6 — Financial Responsibility" plain="This confirms you understand that you (or the insurance policyholder) are responsible for any costs insurance does not cover, such as copays, deductibles, or self-pay fees, for whichever services you receive." text="I acknowledge and accept financial/insurance responsibility for services rendered." testId="financialResponsibility" />
                      <Field label="Item 6 — I acknowledge and accept financial/insurance responsibility for services rendered." name="financialResponsibility" type="checkbox" value={sectionData.financialResponsibility} onChange={(v) => u("financialResponsibility", v)} required />
                    </div>
                    <div>
                      <ConsentDisclosure title="Item 7 — Grievance & Complaint Procedure" plain="This confirms you were given information explaining how to file a complaint or grievance directly with SDORA if you are ever unhappy with your care, and that you also have the separate right to file a complaint with the Maryland Behavioral Health Administration." text="I acknowledge I have received and reviewed SDORA Wellbeing's Grievance and Complaint Procedure, including my right to file a complaint with the Maryland Behavioral Health Administration." testId="grievanceProcedure" />
                      <Field label="Item 7 — I acknowledge I have received and reviewed SDORA Wellbeing's Grievance and Complaint Procedure, including my right to file a complaint with the Maryland Behavioral Health Administration." name="grievanceProcedure" type="checkbox" value={sectionData.grievanceProcedure} onChange={(v) => u("grievanceProcedure", v)} required />
                    </div>
                    <div>
                      <ConsentDisclosure title="Item 8 — Client Rights & Responsibilities" plain="This confirms you received a written explanation of your rights as a client — such as being treated with respect and participating in decisions about your care — and your responsibilities, such as attending scheduled appointments and communicating openly with your care team." text="I acknowledge I have received and reviewed my Client Rights & Responsibilities." testId="clientRights" />
                      <Field label="Item 8 — I acknowledge I have received and reviewed my Client Rights & Responsibilities." name="clientRights" type="checkbox" value={sectionData.clientRights} onChange={(v) => u("clientRights", v)} required />
                    </div>
                  </div>
                  <p className="mt-4 text-xs text-gray-500 italic" data-testid="intake-initials-instruction">
                    Initial each item below. The person signing this packet initials: the client, or the parent or legal guardian signing for the client.
                  </p>
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <ConsentDisclosure title="Item 9 — Service Delivery Preference" plain="SDORA can provide services at our office (onsite), at another location such as your home or school (offsite), or a mix of both (blended). This item records which option was explained to you and which one you are choosing. This choice applies to whichever services — OMHC, PRP, or both — you are receiving." text="I was offered onsite services, offsite services, and blended services, and the differences were explained to me." testId="serviceDeliveryPreference" />
                    <Field
                      label="Item 9 — I was offered onsite services, offsite services, and blended services, and the differences were explained to me. I choose:"
                      name="serviceDeliveryPreference"
                      type="radio"
                      value={sectionData.serviceDeliveryPreference}
                      onChange={(v) => u("serviceDeliveryPreference", v)}
                      required
                      options={["Onsite only", "Offsite only", "Blended"]}
                    />
                    <div className="mt-3 max-w-[140px]">
                      <Field
                        label="Initials"
                        name="serviceDeliveryPreferenceInitials"
                        value={sectionData.serviceDeliveryPreferenceInitials}
                        onChange={(v) => u("serviceDeliveryPreferenceInitials", v.slice(0, 4))}
                        placeholder="Initials"
                        required
                      />
                    </div>
                  </div>
                  <div className={`mt-4 p-4 rounded-lg border border-gray-200 ${prpSelected ? "bg-gray-50" : "bg-gray-100 opacity-60"}`} data-testid="intake-prp-acknowledgment-block">
                    <h3 className="text-sm font-bold text-gray-800 mb-1">Item 10 — About Psychiatric Rehabilitation Services</h3>
                    <p className="text-xs text-blue-700 italic mb-2" data-testid="consent-details-prpAcknowledgment">
                      In plain terms: PRP sessions help build daily-living, coping, and social skills through
                      structured, goal-based activities with our clinical team. This requirement applies only to
                      PRP services — it does not apply if you are only receiving OMHC (outpatient therapy/medication)
                      services.
                    </p>
                    <p className="text-xs text-gray-600 mb-2">
                      Psychiatric Rehabilitation (PRP) is a clinical skills-training program. It is not a treatment
                      program, a social program, a childcare program, or a mentoring program.
                    </p>
                    {prpSelected ? (
                      <>
                        <button
                          type="button"
                          onClick={downloadPRPInfoSheet}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium underline mb-3 inline-block"
                          data-testid="intake-download-prp-info-sheet"
                        >
                          Download PRP Info Sheet (PDF)
                        </button>
                        <Field
                          label="This was explained to me and I received it in writing."
                          name="prpAcknowledgment"
                          type="checkbox"
                          value={sectionData.prpAcknowledgment}
                          onChange={(v) => u("prpAcknowledgment", v)}
                          required
                        />
                        <div className="mt-3 max-w-[140px]">
                          <Field
                            label="Initials"
                            name="prpAcknowledgmentInitials"
                            value={sectionData.prpAcknowledgmentInitials}
                            onChange={(v) => u("prpAcknowledgmentInitials", v.slice(0, 4))}
                            placeholder="Initials"
                            required
                          />
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-gray-500 italic" data-testid="intake-prp-not-applicable">
                        Not applicable: no PRP services selected
                      </p>
                    )}
                  </div>
                </>);
              })()}
            </>)}

            {/* Section 9 Part 2: Final Acknowledgments & Signature (Items 11-12) */}
            {sec.key === "consents" && sec.part === 2 && (<>
              {(() => {
                const isMinor = (formData.personalInfo || {}).isMinor === "Yes";
                const guardianType = (formData.consents || {}).guardianType;
                const legalRepName = (formData.consents || {}).legalRepName;
                const legalRepRelationship = (formData.consents || {}).legalRepRelationship;
                const transportLabel = isMinor
                  ? "Item 11 — As the parent, legal guardian, or court-appointed guardian signing this form, I authorize SDORA Wellbeing staff to transport the client to and from program-related activities and appointments. I understand I may revoke this authorization in writing at any time, and that SDORA Wellbeing will follow all applicable child safety seat, seatbelt, and supervision requirements."
                  : "Item 11 — I authorize SDORA Wellbeing staff to transport me to and from program-related activities and appointments. I understand I may decline transportation services at any time and request alternate arrangements without affecting my eligibility for care.";
                const whoSigns = isMinor
                  ? `Because the client is a minor, this form must be signed by the ${guardianType || "parent or legal guardian"}.`
                  : (formData.consents || {}).hasLegalRepresentative === "Yes"
                    ? `This form may be signed by the client or by ${legalRepName || "the client's legal representative"}${legalRepRelationship ? ` (${legalRepRelationship})` : ""}.`
                    : "This form must be signed by the client.";
                const todayISO = new Date().toISOString().slice(0, 10);
                const isBackdated = sectionData.consentEffectiveDate && sectionData.consentEffectiveDate < todayISO;
                return (<>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="intake-transportation-block">
                    <ConsentDisclosure title="Item 11 — Transportation Authorization" plain="This records whether you want SDORA staff to provide or arrange transportation to program-related appointments and activities. This applies specifically to PRP-related transportation needs. If the client is a minor, this authorization must come from the parent, legal guardian, or court-appointed guardian." text={transportLabel} testId="transportationConsent" />
                    <Field label={transportLabel} name="transportationConsent" type="yesno" value={sectionData.transportationConsent} onChange={(v) => u("transportationConsent", v)} required />
                    <div className="mt-3 max-w-[140px]">
                      <Field label="Initials" name="transportationConsentInitials" value={sectionData.transportationConsentInitials} onChange={(v) => u("transportationConsentInitials", v.slice(0, 4))} placeholder="Initials" required />
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <ConsentDisclosure title="Item 12 — Right to Refuse Treatment" plain="This confirms you understand you can say no to treatment at any time — this applies to OMHC and PRP services alike — without losing access to other services, except in situations required by law, such as court-ordered treatment." text="I acknowledge that I have the right to refuse treatment at any time and that refusal will not affect my eligibility for other services, except as required by law (e.g., court-ordered treatment)." testId="rightToRefuse" />
                    <Field label="Item 12 — I acknowledge that I have the right to refuse treatment at any time and that refusal will not affect my eligibility for other services, except as required by law (e.g., court-ordered treatment)." name="rightToRefuse" type="checkbox" value={sectionData.rightToRefuse} onChange={(v) => u("rightToRefuse", v)} required />
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-bold text-gray-800 mb-1">Release of Information</h3>
                    <p className="text-xs text-gray-500 mb-2">If you would like SDORA Wellbeing to share information with a specific provider or agency, please describe below. This is optional and applies only if you want us to coordinate care with someone outside SDORA, for either OMHC or PRP services. Leaving this blank means no additional release is authorized beyond what is already covered by the Notice of Privacy Practices.</p>
                    <Field label="Release of Information Details" name="releaseOfInfoDetails" type="textarea" value={sectionData.releaseOfInfoDetails} onChange={(v) => u("releaseOfInfoDetails", v)} placeholder="Specify who, what information, and for what purpose" rows={2} />
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="text-sm font-bold text-gray-800 mb-1">Electronic Signature</h3>
                    <p className="text-xs text-blue-700 mb-3" data-testid="intake-who-signs-hint">{whoSigns}</p>
                    <Field label="Type your full legal name as your electronic signature" name="signatureText" value={sectionData.signatureText} onChange={(v) => u("signatureText", v)} required placeholder="Full legal name" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                      <Field label="Printed Name" name="signaturePrintedName" value={sectionData.signaturePrintedName} onChange={(v) => u("signaturePrintedName", v)} required />
                      <Field label="Date" name="signatureDate" type="date" value={sectionData.signatureDate} onChange={(v) => u("signatureDate", v)} required />
                    </div>
                    <Field label="Relationship to Client (if guardian/representative)" name="signatureRelationship" value={sectionData.signatureRelationship} onChange={(v) => u("signatureRelationship", v)} placeholder="e.g. Parent, Legal Guardian" />
                  </div>
                  <div className="p-4 bg-white rounded-lg border-2 border-blue-100" data-testid="consent-effective-date-block">
                    <h3 className="text-sm font-bold text-gray-800 mb-1">Consent Effective Date</h3>
                    <p className="text-xs text-gray-500 mb-3">
                      This is usually today's date. Only enter an earlier date if you have been authorized to document that consent was given on an earlier date (for example, digitizing a previously signed paper form). Your actual electronic signature date/time is separately recorded automatically by the system when you submit and cannot be changed.
                    </p>
                    <Field label="Consent Effective Date" name="consentEffectiveDate" type="date" value={sectionData.consentEffectiveDate} onChange={(v) => u("consentEffectiveDate", v)} required />
                    {isBackdated && (
                      <div className="mt-3">
                        <Field
                          label="Reason for earlier consent effective date"
                          name="effectiveDateReason"
                          type="textarea"
                          value={sectionData.effectiveDateReason}
                          onChange={(v) => u("effectiveDateReason", v)}
                          required
                          placeholder="Document why the consent effective date differs from the electronic signature date."
                          rows={2}
                        />
                      </div>
                    )}
                  </div>
                </>);
              })()}
            </>)}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30 transition-colors"
            data-testid="intake-prev">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          {step < SECTIONS.length - 1 ? (
            <button onClick={handleNext}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors"
              data-testid="intake-next">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-1.5 px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 disabled:opacity-50 transition-colors"
              data-testid="intake-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit Intake
            </button>
          )}
        </div>

        {/* Section nav dots */}
        <div className="flex items-center justify-center gap-1.5 mt-6">
          {SECTIONS.map((s, i) => (
            <button key={`${s.key}-${s.part || 0}`} onClick={() => setStep(i)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${i === step ? "bg-blue-600" : i < step ? "bg-blue-300" : "bg-gray-300"}`}
              title={s.label} data-testid={`intake-dot-${i}`} />
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default IntakePage;
